import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import i18n from "@/i18n";

const LocalizedDataContext = createContext({});

export const useLocalizedData = () => useContext(LocalizedDataContext);

export const LocalizedDataProvider = ({ children }) => {
  const [branches, setBranches] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [measurementParams, setMeasurementParams] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem('pbstudio_language');
    return (stored || i18n.language || "tr").toLowerCase().split("-")[0];
  });
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const handler = (newLang) => {
      setLang((newLang || "tr").toLowerCase().split("-")[0]);
    };
    i18n.on("languageChanged", handler);
    const interval = setInterval(() => {
      const stored = (localStorage.getItem('pbstudio_language') || "tr").toLowerCase().split("-")[0];
      setLang(prev => prev !== stored ? stored : prev);
    }, 1000);
    return () => { i18n.off("languageChanged", handler); clearInterval(interval); };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [branchRes, typesRes, paramsRes, expRes] = await Promise.all([
        fetch(`${API}/api/branches`),
        fetch(`${API}/api/training-types`),
        fetch(`${API}/api/public/measurement-params`),
        fetch(`${API}/api/expense-types`),
      ]);
      if (branchRes.ok) setBranches((await branchRes.json()).map(b => typeof b === "object" ? b : { name: b, translations: {} }));
      if (typesRes.ok) setTrainingTypes((await typesRes.json()).map(t => typeof t === "object" ? t : { name: t, translations: {} }));
      if (paramsRes.ok) { const d = await paramsRes.json(); setMeasurementParams((d.params || d || []).map(p => typeof p === "object" ? p : { name: p, translations: {} })); }
      if (expRes.ok) setExpenseTypes((await expRes.json()).map(e => typeof e === "object" ? e : { name: e, translations: {} }));
    } catch (err) { console.error("LocalizedData fetch error:", err); }
  }, [API]);

  useEffect(() => { if (API) fetchData(); }, [fetchData, API]);

  const getLocalizedBranch = (name) => {
    if (!name || lang === "tr") return name || "";
    const item = branches.find(b => b.name === name || b.id === name);
    return item?.translations?.[lang] || name;
  };

  const getLocalizedTrainingType = (name) => {
    if (!name || lang === "tr") return name || "";
    const item = trainingTypes.find(t => t.name === name || t.id === name || t.label === name);
    return item?.translations?.[lang] || name;
  };

  const getLocalizedMeasurement = (name) => {
    if (!name || lang === "tr") return name || "";
    const item = measurementParams.find(p => p.name === name || p.id === name);
    return item?.translations?.[lang] || name;
  };

  const getLocalizedExpenseType = (nameOrId) => {
    if (!nameOrId || lang === "tr") return nameOrId || "";
    const item = expenseTypes.find(e => e.name === nameOrId || e.id === nameOrId);
    return item?.translations?.[lang] || nameOrId;
  };

  return (
    <LocalizedDataContext.Provider value={{
      branches, trainingTypes, measurementParams, expenseTypes, lang,
      getLocalizedBranch, getLocalizedTrainingType, getLocalizedMeasurement, getLocalizedExpenseType,
      refreshData: fetchData,
    }}>
      {children}
    </LocalizedDataContext.Provider>
  );
};
